import React, { FunctionComponent } from 'react';
import FormSuccessSubmit from '@/components/FormSuccessSubmit/FormSuccessSubmit';
import { Button, Col } from '@visalex/core';
import { emailPattern, useForm } from '@visalex/forms';
import { isValidPhoneNumber } from 'react-phone-number-input';

import { useTranslation } from 'next-i18next';

import styles from './QuestionFormSection.module.scss';

type FormFields = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  question?: string;
};

const transitionPrefix = 'green-cards.question-form';
const formPrefix = 'common.forms';

type GreenCardFormSectionProps = {
  handleSubmit: (formData: FormFields) => Promise<void>;
  isSubmitWithError: boolean;
};

const QuestionFormSection: FunctionComponent<GreenCardFormSectionProps> = ({ handleSubmit, isSubmitWithError }) => {
  const { t } = useTranslation();

  const { Form, formState } = useForm<FormFields>({ onSubmit: handleSubmit });

  return (
    <section className={styles['question-form']}>
      <Col>
        <div className={styles['question-form__header']}>
          <h3 className={styles['question-form__header_title']}>{t(`${transitionPrefix}.title`)}</h3>
          <p className={styles['question-form__header_subtitle']}>{t(`${transitionPrefix}.subtitle`)}</p>
        </div>
        <Form>
          {!formState.isSubmitSuccessful || isSubmitWithError ? (
            <div className={styles['question-form__body']}>
              <Form.Input
                name="first-name"
                rules={{
                  required: t(`${formPrefix}.first-name.rules.required`),
                }}
                placeholder={t(`${formPrefix}.first-name.placeholder`)}
              />
              <Form.Input
                name="last-name"
                rules={{
                  required: t(`${formPrefix}.last-name.rules.required`),
                }}
                placeholder={t(`${formPrefix}.last-name.placeholder`)}
              />
              <Form.Input
                name="email"
                rules={{
                  required: t(`${formPrefix}.email.rules.required`),
                  pattern: {
                    value: emailPattern,
                    message: t(`${formPrefix}.email.rules.format`),
                  },
                }}
                placeholder={t(`${formPrefix}.email.placeholder`)}
              />
              <Form.PhoneInput
                name="phone"
                rules={{
                  required: t(`${formPrefix}.phone.rules.required`),
                  validate: (value) => isValidPhoneNumber(value) || t(`${formPrefix}.phone.rules.format`),
                }}
                placeholder={t(`${formPrefix}.phone.placeholder`)}
              />
              <Form.TextArea
                name="question"
                rules={{
                  required: t(`${formPrefix}.question.rules.required`),
                }}
                placeholder={t(`${formPrefix}.question.placeholder`)}
              />

              <div className={styles['question-form__body__controls']}>
                <Button type="submit" className={styles['question-form__body__controls_button']}>
                  {t(`${transitionPrefix}.button-text`)}
                </Button>
                <div className={styles['question-form__body__controls_description']}>
                  {t(`${transitionPrefix}.confidentiality-text`)}
                </div>
              </div>
            </div>
          ) : (
            <FormSuccessSubmit description={t('form-success-submit.description')} />
          )}
        </Form>
      </Col>
    </section>
  );
};

export default QuestionFormSection;
